import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";
import { statusLabels, benefitTypeLabels, insuranceTypeLabels, relationshipLabels } from "../../lib/constants";

// ==================== BENEFITS PAGE ====================
const BenefitsPage = () => {
  const [activeTab, setActiveTab] = useState('summary');
  const [summary, setSummary] = useState(null);
  const [family, setFamily] = useState([]);
  const [insurance, setInsurance] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [childcare, setChildcare] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [showBenefitModal, setShowBenefitModal] = useState(false);
  const [showChildcareModal, setShowChildcareModal] = useState(false);
  const { user } = useAuth();
  const isAdmin = ['super_admin', 'tenant_admin', 'hr_manager'].includes(user?.role);

  const fetchData = async () => {
    try {
      const [summaryRes, familyRes, insuranceRes, benefitsRes, childcareRes] = await Promise.all([
        api.get('/portal/benefits/summary'),
        api.get('/portal/family'),
        api.get('/portal/insurance'),
        api.get('/portal/benefits'),
        api.get('/portal/childcare')
      ]);
      setSummary(summaryRes.data);
      setFamily(familyRes.data);
      setInsurance(insuranceRes.data);
      setBenefits(benefitsRes.data);
      setChildcare(childcareRes.data);
    } catch (error) {
      console.error('Error fetching benefits data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDeleteFamily = async (memberId) => {
    if (!window.confirm('Tem a certeza que deseja remover este membro?')) return;
    try {
      await api.delete(`/portal/family/${memberId}`);
      toast.success('Membro removido');
      fetchData();
    } catch (error) {
      toast.error('Erro ao remover membro');
    }
  };

  const handleApproveChildcare = async (voucherId, approved) => {
    try {
      await api.put(`/portal/childcare/${voucherId}/approve`, { approved });
      toast.success(approved ? 'Cheque creche aprovado' : 'Cheque creche rejeitado');
      fetchData();
    } catch (error) {
      toast.error('Erro ao processar aprovação');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="benefits-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Benefícios e Subsídios</h1>
          <p className="text-muted-foreground">Gestão de seguros, agregado familiar e benefícios</p>
        </div>
      </div>

      <div className="flex gap-2 border-b overflow-x-auto pb-px">
        {[
          { id: 'summary', label: 'Resumo' },
          { id: 'family', label: 'Agregado Familiar' },
          { id: 'insurance', label: 'Seguros' },
          { id: 'benefits', label: 'Benefícios' },
          { id: 'childcare', label: 'Cheques Creche' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'summary' && summary && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-xl border bg-card p-5 cursor-pointer hover:border-primary/50" onClick={() => setActiveTab('family')}>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
            </div>
            <p className="text-3xl font-bold">{summary.family?.total_members || 0}</p>
            <p className="text-sm text-muted-foreground">Membros do Agregado</p>
          </div>
          <div className="rounded-xl border bg-card p-5 cursor-pointer hover:border-primary/50" onClick={() => setActiveTab('insurance')}>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
            </div>
            <p className="text-3xl font-bold">{summary.insurance?.active_policies || 0}</p>
            <p className="text-sm text-muted-foreground">Seguros Ativos</p>
            <p className="text-xs text-primary mt-1">{summary.insurance?.total_monthly_premium?.toFixed(2) || 0}€/mês</p>
          </div>
          <div className="rounded-xl border bg-card p-5 cursor-pointer hover:border-primary/50" onClick={() => setActiveTab('benefits')}>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" /></svg>
            </div>
            <p className="text-3xl font-bold">{summary.benefits?.active_benefits || 0}</p>
            <p className="text-sm text-muted-foreground">Benefícios Ativos</p>
            <p className="text-xs text-primary mt-1">{summary.benefits?.total_monthly_value?.toFixed(2) || 0}€/mês</p>
          </div>
          <div className="rounded-xl border bg-card p-5 cursor-pointer hover:border-primary/50" onClick={() => setActiveTab('childcare')}>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 to-red-500 flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
            </div>
            <p className="text-3xl font-bold">{summary.childcare?.approved_vouchers_this_year || 0}</p>
            <p className="text-sm text-muted-foreground">Cheques Creche (este ano)</p>
            <p className="text-xs text-primary mt-1">{summary.childcare?.total_amount_this_year?.toFixed(2) || 0}€ total</p>
          </div>
        </div>
      )}

      {activeTab === 'family' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => setShowFamilyModal(true)} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium flex items-center gap-2" data-testid="add-family-btn">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Adicionar Membro
            </button>
          </div>
          {family.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border"><p className="text-muted-foreground">Sem membros do agregado registados</p></div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {family.map(member => (
                <div key={member.member_id} className="rounded-xl border bg-card p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-semibold">
                      {member.name?.charAt(0).toUpperCase()}
                    </div>
                    <button onClick={() => handleDeleteFamily(member.member_id)} className="p-1.5 rounded-lg hover:bg-red-100 text-red-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  </div>
                  <h3 className="font-semibold text-lg">{member.name}</h3>
                  <p className="text-sm text-muted-foreground">{relationshipLabels[member.relationship] || member.relationship}</p>
                  {member.birth_date && <p className="text-sm text-muted-foreground mt-1">Nascimento: {member.birth_date}</p>}
                  {member.nif && <p className="text-sm text-muted-foreground">NIF: {member.nif}</p>}
                  {member.education_level && <p className="text-sm text-muted-foreground">Educação: {member.education_level}</p>}
                  {member.school_name && <p className="text-sm text-primary">{member.school_name}</p>}
                  <div className="flex gap-2 mt-3">
                    {member.is_dependent && <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-700">Dependente</span>}
                    {member.special_needs && <span className="px-2 py-1 rounded text-xs bg-amber-100 text-amber-700">Necessidades Especiais</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'insurance' && (
        <div className="space-y-4">
          {isAdmin && (
            <div className="flex justify-end">
              <button onClick={() => setShowInsuranceModal(true)} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium flex items-center gap-2" data-testid="add-insurance-btn">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Adicionar Seguro
              </button>
            </div>
          )}
          {insurance.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border"><p className="text-muted-foreground">Sem seguros registados</p></div>
          ) : (
            <div className="space-y-4">
              {insurance.map(policy => (
                <div key={policy.insurance_id} className="rounded-xl border bg-card p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        policy.insurance_type === 'saude' ? 'bg-emerald-100 text-emerald-700' :
                        policy.insurance_type === 'vida' ? 'bg-purple-100 text-purple-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>{insuranceTypeLabels[policy.insurance_type] || policy.insurance_type}</span>
                      <h3 className="font-semibold text-lg mt-2">{policy.provider}</h3>
                      <p className="text-sm text-muted-foreground">Apólice: {policy.policy_number || '-'}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${policy.status === 'ativo' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {statusLabels[policy.status] || policy.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
                    <div><p className="text-sm text-muted-foreground">Prémio Mensal</p><p className="font-medium">{policy.monthly_premium?.toFixed(2)}€</p></div>
                    <div><p className="text-sm text-muted-foreground">Empresa Paga</p><p className="font-medium text-emerald-600">{policy.employer_contribution}%</p></div>
                    <div><p className="text-sm text-muted-foreground">Colaborador Paga</p><p className="font-medium">{policy.employee_contribution}%</p></div>
                    <div><p className="text-sm text-muted-foreground">Cobertura</p><p className="font-medium capitalize">{policy.coverage_type}</p></div>
                  </div>
                  <div className="flex gap-4 mt-3 text-sm text-muted-foreground">
                    <span>Início: {policy.start_date}</span>
                    {policy.renewal_date && <span>Renovação: {policy.renewal_date}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'benefits' && (
        <div className="space-y-4">
          {isAdmin && (
            <div className="flex justify-end">
              <button onClick={() => setShowBenefitModal(true)} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium flex items-center gap-2" data-testid="add-benefit-btn">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                Adicionar Benefício
              </button>
            </div>
          )}
          {benefits.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border"><p className="text-muted-foreground">Sem benefícios registados</p></div>
          ) : (
            <div className="rounded-xl border bg-card overflow-hidden">
              <table className="data-table">
                <thead><tr><th>Tipo</th><th>Descrição</th><th>Valor Mensal</th><th>Limite Anual</th><th>Estado</th></tr></thead>
                <tbody>
                  {benefits.map(b => (
                    <tr key={b.benefit_id}>
                      <td><span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-700">{benefitTypeLabels[b.benefit_type] || b.benefit_type}</span></td>
                      <td>{b.description || '-'}</td>
                      <td className="font-medium text-primary">{b.monthly_value?.toFixed(2)}€</td>
                      <td>{b.annual_limit ? `${b.annual_limit}€` : '-'}</td>
                      <td><span className={`px-2 py-1 rounded-full text-xs font-medium ${b.status === 'ativo' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{statusLabels[b.status]}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'childcare' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button onClick={() => setShowChildcareModal(true)} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium flex items-center gap-2" data-testid="add-childcare-btn">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Pedir Cheque Creche
            </button>
          </div>
          {childcare.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border"><p className="text-muted-foreground">Sem pedidos de cheques creche</p></div>
          ) : (
            <div className="rounded-xl border bg-card overflow-hidden">
              <table className="data-table">
                <thead><tr><th>Criança</th><th>Instituição</th><th>Mês/Ano</th><th>Valor</th><th>Estado</th><th>Ações</th></tr></thead>
                <tbody>
                  {childcare.map(v => (
                    <tr key={v.voucher_id}>
                      <td className="font-medium">{v.child_name}</td>
                      <td>{v.institution_name}</td>
                      <td>{v.month}/{v.year}</td>
                      <td className="font-medium text-primary">{v.monthly_value?.toFixed(2)}€</td>
                      <td>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          v.status === 'aprovado' || v.status === 'pago' ? 'bg-emerald-100 text-emerald-700' :
                          v.status === 'rejeitado' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>{statusLabels[v.status] || v.status}</span>
                      </td>
                      <td>
                        {isAdmin && v.status === 'pendente' && (
                          <div className="flex items-center gap-2">
                            <button onClick={() => handleApproveChildcare(v.voucher_id, true)} className="p-1.5 rounded-lg hover:bg-emerald-100 text-emerald-600" title="Aprovar">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            </button>
                            <button onClick={() => handleApproveChildcare(v.voucher_id, false)} className="p-1.5 rounded-lg hover:bg-red-100 text-red-600" title="Rejeitar">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {showFamilyModal && <AddFamilyMemberModal onClose={() => setShowFamilyModal(false)} onCreated={() => { setShowFamilyModal(false); fetchData(); }} />}
      {showInsuranceModal && <AddInsuranceModal onClose={() => setShowInsuranceModal(false)} onCreated={() => { setShowInsuranceModal(false); fetchData(); }} />}
      {showBenefitModal && <AddBenefitModal onClose={() => setShowBenefitModal(false)} onCreated={() => { setShowBenefitModal(false); fetchData(); }} />}
      {showChildcareModal && <AddChildcareModal family={family} onClose={() => setShowChildcareModal(false)} onCreated={() => { setShowChildcareModal(false); fetchData(); }} />}
    </div>
  );
};

// Modal Components
const AddFamilyMemberModal = ({ onClose, onCreated }) => {
  const [formData, setFormData] = useState({ name: '', relationship: 'filho', birth_date: '', nif: '', gender: '', education_level: '', school_name: '', is_dependent: true, special_needs: false });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/portal/family', formData);
      toast.success('Membro do agregado adicionado');
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar membro');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-card">
          <h3 className="text-lg font-semibold">Adicionar Membro do Agregado</h3>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div><label className="block text-sm font-medium mb-1">Nome Completo *</label><input type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Parentesco *</label>
              <select value={formData.relationship} onChange={e => setFormData({...formData, relationship: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
                <option value="conjuge">Cônjuge</option><option value="filho">Filho</option><option value="filha">Filha</option><option value="pai">Pai</option><option value="mae">Mãe</option><option value="outro">Outro</option>
              </select>
            </div>
            <div><label className="block text-sm font-medium mb-1">Data Nascimento</label><input type="date" value={formData.birth_date} onChange={e => setFormData({...formData, birth_date: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">NIF</label><input type="text" value={formData.nif} onChange={e => setFormData({...formData, nif: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
            <div><label className="block text-sm font-medium mb-1">Género</label>
              <select value={formData.gender} onChange={e => setFormData({...formData, gender: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
                <option value="">Selecionar...</option><option value="masculino">Masculino</option><option value="feminino">Feminino</option><option value="outro">Outro</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Nível Educação</label>
              <select value={formData.education_level} onChange={e => setFormData({...formData, education_level: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
                <option value="">N/A</option><option value="creche">Creche</option><option value="pre_escolar">Pré-Escolar</option><option value="basico">Básico</option><option value="secundario">Secundário</option><option value="superior">Superior</option>
              </select>
            </div>
            <div><label className="block text-sm font-medium mb-1">Estabelecimento</label><input type="text" value={formData.school_name} onChange={e => setFormData({...formData, school_name: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" placeholder="Nome da escola/creche" /></div>
          </div>
          <div className="flex gap-4">
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.is_dependent} onChange={e => setFormData({...formData, is_dependent: e.target.checked})} className="rounded" />Dependente fiscal</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={formData.special_needs} onChange={e => setFormData({...formData, special_needs: e.target.checked})} className="rounded" />Necessidades especiais</label>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border hover:bg-muted">Cancelar</button>
            <button type="submit" disabled={loading} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium disabled:opacity-50">{loading ? 'A guardar...' : 'Adicionar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddInsuranceModal = ({ onClose, onCreated }) => {
  const [formData, setFormData] = useState({ insurance_type: 'saude', provider: '', policy_number: '', coverage_type: 'individual', monthly_premium: '', employer_contribution: 100, start_date: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/portal/insurance', { ...formData, monthly_premium: parseFloat(formData.monthly_premium) || 0, employee_contribution: 100 - formData.employer_contribution });
      toast.success('Seguro adicionado');
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar seguro');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">Adicionar Seguro</h3>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Tipo *</label>
              <select value={formData.insurance_type} onChange={e => setFormData({...formData, insurance_type: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
                <option value="saude">Saúde</option><option value="vida">Vida</option><option value="acidentes_trabalho">Acidentes de Trabalho</option><option value="dental">Dental</option><option value="visao">Visão</option>
              </select>
            </div>
            <div><label className="block text-sm font-medium mb-1">Cobertura</label>
              <select value={formData.coverage_type} onChange={e => setFormData({...formData, coverage_type: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
                <option value="individual">Individual</option><option value="familiar">Familiar</option>
              </select>
            </div>
          </div>
          <div><label className="block text-sm font-medium mb-1">Seguradora *</label><input type="text" required value={formData.provider} onChange={e => setFormData({...formData, provider: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" placeholder="Ex: Fidelidade, Allianz..." /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Nº Apólice</label><input type="text" value={formData.policy_number} onChange={e => setFormData({...formData, policy_number: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
            <div><label className="block text-sm font-medium mb-1">Prémio Mensal (€) *</label><input type="number" step="0.01" required value={formData.monthly_premium} onChange={e => setFormData({...formData, monthly_premium: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
          </div>
          <div><label className="block text-sm font-medium mb-1">Empresa Paga (%): {formData.employer_contribution}%</label><input type="range" min="0" max="100" value={formData.employer_contribution} onChange={e => setFormData({...formData, employer_contribution: parseInt(e.target.value)})} className="w-full" /></div>
          <div><label className="block text-sm font-medium mb-1">Data Início *</label><input type="date" required value={formData.start_date} onChange={e => setFormData({...formData, start_date: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border hover:bg-muted">Cancelar</button>
            <button type="submit" disabled={loading} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium disabled:opacity-50">{loading ? 'A guardar...' : 'Adicionar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddBenefitModal = ({ onClose, onCreated }) => {
  const [formData, setFormData] = useState({ benefit_type: 'subsidio_alimentacao', description: '', monthly_value: '', annual_limit: '', start_date: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/portal/benefits', { ...formData, monthly_value: parseFloat(formData.monthly_value) || 0, annual_limit: formData.annual_limit ? parseFloat(formData.annual_limit) : null });
      toast.success('Benefício adicionado');
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar benefício');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">Adicionar Benefício</h3>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div><label className="block text-sm font-medium mb-1">Tipo *</label>
            <select value={formData.benefit_type} onChange={e => setFormData({...formData, benefit_type: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
              <option value="subsidio_alimentacao">Subsídio de Alimentação</option><option value="subsidio_transporte">Subsídio de Transporte</option><option value="gym">Ginásio</option><option value="outro">Outro</option>
            </select>
          </div>
          <div><label className="block text-sm font-medium mb-1">Descrição</label><input type="text" value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm font-medium mb-1">Valor Mensal (€) *</label><input type="number" step="0.01" required value={formData.monthly_value} onChange={e => setFormData({...formData, monthly_value: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
            <div><label className="block text-sm font-medium mb-1">Limite Anual (€)</label><input type="number" step="0.01" value={formData.annual_limit} onChange={e => setFormData({...formData, annual_limit: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
          </div>
          <div><label className="block text-sm font-medium mb-1">Data Início *</label><input type="date" required value={formData.start_date} onChange={e => setFormData({...formData, start_date: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border hover:bg-muted">Cancelar</button>
            <button type="submit" disabled={loading} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium disabled:opacity-50">{loading ? 'A guardar...' : 'Adicionar'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AddChildcareModal = ({ family, onClose, onCreated }) => {
  const children = family.filter(m => ['filho', 'filha'].includes(m.relationship));
  const [formData, setFormData] = useState({ child_member_id: children[0]?.member_id || '', institution_name: '', institution_nif: '', monthly_value: '', year: new Date().getFullYear(), month: new Date().getMonth() + 1 });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.child_member_id) {
      toast.error('Por favor, adicione primeiro uma criança ao agregado familiar');
      return;
    }
    setLoading(true);
    try {
      await api.post('/portal/childcare', { ...formData, monthly_value: parseFloat(formData.monthly_value) || 0 });
      toast.success('Pedido de cheque creche criado');
      onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar pedido');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-card rounded-xl border w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold">Pedir Cheque Creche</h3>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {children.length === 0 ? (
            <div className="p-4 rounded-lg bg-amber-50 text-amber-700 text-sm">
              <p className="font-medium">Sem filhos registados</p>
              <p>Por favor, adicione primeiro os seus filhos no Agregado Familiar.</p>
            </div>
          ) : (
            <>
              <div><label className="block text-sm font-medium mb-1">Criança *</label>
                <select value={formData.child_member_id} onChange={e => setFormData({...formData, child_member_id: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background">
                  {children.map(c => <option key={c.member_id} value={c.member_id}>{c.name}</option>)}
                </select>
              </div>
              <div><label className="block text-sm font-medium mb-1">Instituição (Creche/Jardim) *</label><input type="text" required value={formData.institution_name} onChange={e => setFormData({...formData, institution_name: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
              <div><label className="block text-sm font-medium mb-1">NIF da Instituição</label><input type="text" value={formData.institution_nif} onChange={e => setFormData({...formData, institution_nif: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><label className="block text-sm font-medium mb-1">Mês *</label><input type="number" min="1" max="12" required value={formData.month} onChange={e => setFormData({...formData, month: parseInt(e.target.value)})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
                <div><label className="block text-sm font-medium mb-1">Ano *</label><input type="number" min="2020" required value={formData.year} onChange={e => setFormData({...formData, year: parseInt(e.target.value)})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
                <div><label className="block text-sm font-medium mb-1">Valor (€) *</label><input type="number" step="0.01" required value={formData.monthly_value} onChange={e => setFormData({...formData, monthly_value: e.target.value})} className="w-full px-3 py-2 rounded-lg border border-input bg-background" /></div>
              </div>
            </>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border hover:bg-muted">Cancelar</button>
            <button type="submit" disabled={loading || children.length === 0} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium disabled:opacity-50">{loading ? 'A enviar...' : 'Pedir'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BenefitsPage;
